/**
 * Desktop Discover (lane D2). INVENTORY-DESKTOP §3.3, §6.3, §6.4;
 * `design/Games Tonight.dc.html` lines 113-183.
 *
 * SELECTORS   `discoverVM(s)` — `q`, `hasQ`, `matchLine`, `results[] (TileVM)`,
 *                               `noResults`, `sortVal`, `isGallery`, `isList`
 *             `DESKTOP_SORT_OPTS` (`selectors.desktop.ts`) — the one relabelled
 *                               option, `SORT: PRICE LOW→HIGH`
 *             `viewSeg(view)` (`selectors.3c.ts`) for the ▦ / ≡ pair
 * ACTIONS     `setQ` · `clearQ` · `setSort` · `setView` · `queueAssets`
 *             (`openGame` / `quickAdd` are called from the two row components)
 *
 * LAYOUT (§1.3). Hero column `padding:34px 20px 4px; gap:14px`, box
 * `width:720px; max-width:100%` with the 2px accent border and the double
 * box-shadow. Control row `padding:24px 26px 0; gap:12px; flex-wrap:wrap` =
 * `RESULTS` · FilterBar rail · spacer · native `<select>` + `▾` · two 32×32 view
 * buttons. Gallery `repeat(auto-fill,minmax(168px,1fr)); gap:14px;
 * padding:16px 26px 34px` — 6 columns at 1280px. List `gap:8px`, same padding.
 *
 * THE SEARCH BOX IS DELIBERATELY NOT `<SearchBox>` — same call as mobile
 * (INVENTORY §11): the prototype inlines it here so it can carry the 2px accent
 * border, the glow shadow and the ✕ clear, none of which `SearchBox` has.
 *
 * FILTER BAR: `<FilterBar/>` is the trigger + active-chip rail ONLY. The 960px
 * panel is mounted once by `DesktopApp` off `s.fltOpen` (D0-06) — a
 * `position:fixed` child of this screen would be clipped by the `transform`ed
 * hover tiles above it. Do not render `FilterPanel` here, and do not re-render
 * the chip rail from `v.fltChips` the way the mobile screen does.
 *
 * `/` HOTKEY (§2.4): `useSearchInputRef()` registers the input; `DesktopApp`
 * owns the window listener. Typing `/` while the input already has focus inserts
 * a slash, which is the prototype's own guard.
 *
 * LIVE SEARCH is unchanged from mobile — `setQ` drives the Steam/CheapShark
 * lookup and `matchLine` reports it (`… · SEARCHING LIVE…` / `· N LIVE VIA
 * CHEAPSHARK` / `· LIVE SEARCH OFFLINE`).
 */

import { useQueueArt } from '../../components/useArt.ts';
import type { SortMode } from '../../lib/types.ts';
import { useQd } from '../../store/index.ts';
import { viewSeg } from '../../store/selectors.3c.ts';
import { DESKTOP_SORT_OPTS } from '../../store/selectors.desktop.ts';
import { discoverVM } from '../../store/selectors.ts';
import FilterBar from '../components/FilterBar.tsx';
import ResultRow from '../components/ResultRow.tsx';
import ResultTile from '../components/ResultTile.tsx';
import { useSearchInputRef } from '../searchHotkey.ts';

export default function Discover(): JSX.Element {
  const s = useQd();
  const v = discoverVM(s);
  const seg = viewSeg(s.view);
  const searchRef = useSearchInputRef();

  // §5.3: one batched Steam art lookup per screenful, coalesced 500 ms. Same
  // effect the mobile screen runs — the desktop grid just shows ~6 columns of
  // it at once instead of 2.
  useQueueArt(v.results.map((g) => g.id));

  return (
    <div data-screen-label="Discover" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* --- search hero ------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '34px 20px 4px' }}>
        <div
          style={{
            width: 720,
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            border: '2px solid var(--acc)',
            background: 'var(--gt-card)',
            padding: '13px 16px',
            boxSizing: 'border-box',
            boxShadow: '0 0 0 4px rgba(0,0,0,.55),0 0 30px var(--accSoft)',
          }}
        >
          <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 16, color: 'var(--acc)' }}>▸</span>
          <input
            ref={searchRef}
            value={v.q}
            onChange={(e) => s.setQ(e.target.value)}
            placeholder="SEARCH GAMES, DEVELOPERS, TAGS…"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 0,
              outline: 'none',
              color: 'var(--gt-text)',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 15,
              padding: 0,
            }}
          />
          {v.hasQ ? (
            <span
              onClick={() => s.clearQ()}
              style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 13, color: 'var(--gt-muted)', cursor: 'pointer' }}
            >
              ✕
            </span>
          ) : null}
        </div>
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 10.5,
            letterSpacing: '.06em',
            color: 'var(--gt-muted)',
            textAlign: 'center',
          }}
        >
          {v.matchLine}
        </div>
        {/* The personalisation state line — see the note in the mobile screen.
            Same copy, same rule: the fallback must announce itself. */}
        {v.hasFeedTxt ? (
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 10,
              letterSpacing: '.06em',
              color: v.isFallback ? 'var(--acc)' : 'var(--gt-muted-2)',
              textAlign: 'center',
              marginTop: 6,
            }}
          >
            {v.feedTxt}
          </div>
        ) : null}
        {/* The controls Steam's search cannot answer — same copy and same rule
            as the mobile screen. See the note there. */}
        {v.hasGapTxt ? (
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 10,
              letterSpacing: '.06em',
              color: 'var(--acc)',
              textAlign: 'center',
              marginTop: 6,
            }}
          >
            {v.gapTxt}
          </div>
        ) : null}
      </div>

      {/* --- control row -------------------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '24px 26px 0', flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 8, color: 'var(--gt-muted-2)' }}>RESULTS</div>
        {/* One control. Tags are the panel's last section — see FilterBar.tsx. */}
        <FilterBar />
        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative' }}>
          <select
            value={v.sortVal}
            onChange={(e) => s.setSort(e.target.value as SortMode)}
            style={{
              background: 'var(--gt-field)',
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-3)',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 11,
              padding: '9px 26px 9px 10px',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            {DESKTOP_SORT_OPTS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.t}
              </option>
            ))}
          </select>
          <span
            style={{
              position: 'absolute',
              right: 9,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--gt-muted)',
              fontSize: 9,
              pointerEvents: 'none',
            }}
          >
            ▾
          </span>
        </div>
        <div style={{ display: 'flex' }}>
          <div
            onClick={() => s.setView('g')}
            style={{
              width: 32,
              height: 32,
              border: '1px solid ' + seg.g.bd,
              color: seg.g.clr,
              background: seg.g.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 14,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            ▦
          </div>
          <div
            onClick={() => s.setView('l')}
            style={{
              width: 32,
              height: 32,
              // Longhands, not `border` + `borderLeft:0` — same pixels, but the
              // shorthand/longhand mix makes React warn on every toggle because
              // `seg.l.bd` changes on re-render. Same call the mobile screen
              // made (P3d D-4).
              borderTop: '1px solid ' + seg.l.bd,
              borderRight: '1px solid ' + seg.l.bd,
              borderBottom: '1px solid ' + seg.l.bd,
              borderLeft: 0,
              color: seg.l.clr,
              background: seg.l.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 14,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            ≡
          </div>
        </div>
      </div>

      {/* --- results ------------------------------------------------------ */}
      {v.isGallery ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(168px,1fr))',
            gap: 14,
            padding: '16px 26px 34px',
          }}
        >
          {v.results.map((g) => (
            <ResultTile key={g.key} g={g} />
          ))}
        </div>
      ) : null}

      {v.isList ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 26px 34px' }}>
          {v.results.map((g) => (
            <ResultRow key={g.key} g={g} />
          ))}
        </div>
      ) : null}

      {v.hasMore ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0 26px 34px' }}>
          <div
            onClick={() => (v.isBrowse ? void s.fetchBrowse({ more: true }) : s.loadMoreDiscover())}
            style={{
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-3)',
              background: 'var(--gt-field)',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 11,
              padding: '11px 30px',
              cursor: 'pointer',
            }}
          >
            {v.moreTxt}
          </div>
        </div>
      ) : null}

      {v.cappedTxt ? (
        <div
          style={{
            padding: '0 26px 34px',
            textAlign: 'center',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 10.5,
            letterSpacing: '.06em',
            color: 'var(--gt-muted)',
          }}
        >
          {v.cappedTxt}
        </div>
      ) : null}

      {v.noResults ? (
        <div
          style={{
            padding: '60px 26px',
            textAlign: 'center',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 12,
            color: 'var(--gt-muted)',
          }}
        >
          {v.emptyTxt}
        </div>
      ) : null}

      {/* Decision 5 — see the mobile screen for the reasoning. */}
      {v.hasHidden ? (
        <div
          style={{
            padding: v.noResults ? '0 26px 40px' : '0 26px 34px',
            textAlign: 'center',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 10.5,
            letterSpacing: '.06em',
            color: 'var(--acc2)',
          }}
        >
          {v.hiddenTxt}
        </div>
      ) : null}
    </div>
  );
}
